import { useState } from "react";
import TabControl from "../components/TabControl";
import ColorMapper from "../components/CharacterCreator/ColorMapper";
import SpriteCanvas from "../components/CharacterCreator/SpriteCanvas";
import { CharacterOptions } from "../components/CharacterCreator/CharacterOptions/CharacterOptions";
import { useCustomization } from "../contexts/CustomizationContext";
import ChangeBanner from "../components/CharacterCreator/ChangeBanner";
import TextOptions from "../components/CharacterCreator/TextOptions/TextOptions";
import { usePixl } from "../contexts/PixlContext";
import {
    ItemData,
    AvatarCustomization,
    BannerData,
    type ItemDatabaseMethod,
    FontStyle,
} from "@mythic/pixl";

import "./CharacterCreator.css";
import TitleBar from "../components/TitleBar/TitleBar";

const TAB_NAMES = [
    "Text",
    "Banner",
    "Head",
    "Eyes",
    "Mouths",
    "Bodies",
    "Clothes",
    "Head Acc",
    "Face Acc.",
    "Body Acc.",
    "Back Acc.",
    "Effects",
];
export default function CharacterCreator() {
    const [activeTab, setActiveTab] = useState(0); // Tracks the current tab.
    const { pixlDb } = usePixl();

    const {
        customization,
        setCustomization,
        changesMade,
        font,
        setFont,
        banner,
        setBanner,
        categoryMap,
    } = useCustomization();

    const handleSelection = (sel: number) => {
        if (!pixlDb || !customization) return;

        // Use the tab to get the correct category
        const mapping = categoryMap[activeTab];
        if (!mapping) return;

        // get the category items
        const data = (
            pixlDb[mapping.method as ItemDatabaseMethod] as () => ItemData[]
        )();

        // Get the item using the selection.
        const itemData =
            sel > -1 ? data[sel] : { id: null, defaultColorMap: [] };

        // Compile a new color map from the user's existing color map
        // and the defaultColorMap for the item. This ensures the requisite
        // item always has enough colors for the zones.
        const colorMap = itemData.defaultColorMap
            ? itemData.defaultColorMap.map((defaultColor, index) => {
                  if (
                      customization[mapping.key] &&
                      customization[mapping.key].colorMap
                  )
                      return (
                          customization[mapping.key].colorMap[index] ??
                          defaultColor
                      );
                  return defaultColor;
              })
            : [];

        setCustomization({
            ...customization,
            [mapping.key]: {
                id: itemData.id,
                colorMap,
            },
        }); //result);
    };

    const handleBannerSelection = (sel: number) => {
        // Use the tab to get the correct category
        const mapping = categoryMap[activeTab];
        if (!mapping || !pixlDb) return;

        // get the category items
        //const data = window.SpriteGenerator[mapping.method]();
        //const data = pixlDb[mapping.method]();
        const data = (
            pixlDb[mapping.method as ItemDatabaseMethod] as () => ItemData[]
        )();
        // Get the item using the selection.
        const itemData =
            sel > -1 ? data[sel] : { id: null, defaultColorMap: [] };

        // Compile a new color map from the user's existing color map
        // and the defaultColorMap for the item. This ensures the requisite
        // item always has enough colors for the zones.
        const colorMap = banner?.colorMap ?? [];

        itemData.defaultColorMap
            ? itemData.defaultColorMap.map((defaultColor, idx) => {
                  if (idx >= colorMap.length) colorMap.push(defaultColor);
              })
            : [];

        setBanner({
            id: itemData?.id ?? "",
            colorMap: colorMap,
        });
    };

    // When the color mapper is used on anything that isn't the skin
    const handleColor = (colorMap: string[]) => {
        // Use the tab to get the correct category
        const mapping = categoryMap[activeTab];
        if (!mapping) return;

        // Create new customization data
        const result = { ...customization } as AvatarCustomization;

        // Set the color map for the category
        result[mapping.key].colorMap = colorMap;

        // Set the customization data.
        setCustomization(result);
    };

    // When the color mapper is used on anything that isn't the skin
    const handleBannerColor = (colorMap: string[]) => {
        // Use the tab to get the correct category
        const mapping = categoryMap[activeTab];
        if (!mapping) return;

        // Create new customization data
        const result = { ...banner } as BannerData;

        // Set the color map for the category
        result.colorMap = colorMap;

        // Set the customization data.
        setBanner(result);
    };

    // When the color mapper is used on the skin.
    const handleSkinColor = (colorMap: string[]) => {
        // Create new customization data
        const result = {
            ...customization,
            skin_color: colorMap[0],
        } as AvatarCustomization;
        setCustomization(result);
    };

    return (
        <>
            <TitleBar title="Mythic Avatar Creator" />
            <div style={{ maxWidth: "var(--app-max-width)", width: "100%" }}>
                {changesMade && <ChangeBanner />}
                <TabControl
                    tabs={TAB_NAMES}
                    onTabChange={(tab) => {
                        setActiveTab(tab);
                    }}
                />
                <div className={"character-creator-body"}>
                    <SpriteCanvas>
                        {customization &&
                            (() => {
                                const categoryKey = categoryMap[activeTab]?.key;
                                const item = pixlDb?.getItem(
                                    categoryKey,
                                    customization[categoryKey]?.id,
                                );
                                if (categoryKey === "banner") {
                                    const bannerItem = pixlDb?.getItem(
                                        "banner",
                                        banner?.id ?? "",
                                    );

                                    if (bannerItem?.colorStyle !== "set")
                                        if (banner && banner.colorMap)
                                            return (
                                                <ColorMapper
                                                    colorMap={banner.colorMap}
                                                    onChange={handleBannerColor}
                                                />
                                            );
                                } else if (categoryKey === "text") {
                                    return (
                                        <ColorMapper
                                            colorMap={[
                                                font?.color ?? "#FFFFFF",
                                            ]}
                                            onChange={(map) => {
                                                setFont({
                                                    ...font,
                                                    color: map[0],
                                                } as FontStyle);
                                            }}
                                        />
                                    );
                                } else if (item) {
                                    switch (item?.colorStyle) {
                                        case "skin":
                                            return (
                                                <ColorMapper
                                                    colorMap={[
                                                        customization.skin_color,
                                                    ]}
                                                    onChange={handleSkinColor}
                                                />
                                            );
                                        case "uniform":
                                        case "zones":
                                            return (
                                                <ColorMapper
                                                    colorMap={
                                                        customization[
                                                            categoryKey
                                                        ]?.colorMap
                                                    }
                                                    onChange={handleColor}
                                                />
                                            );
                                    }
                                }
                                return <ColorMapper colorMap={[]} />;
                            })()}
                    </SpriteCanvas>
                    {customization && activeTab === 0 && <TextOptions />}
                    {customization && activeTab === 1 && (
                        <CharacterOptions
                            category={activeTab}
                            onSelection={handleBannerSelection}
                            allowNull={true}
                        />
                    )}
                    {customization && activeTab > 1 && (
                        <CharacterOptions
                            category={activeTab}
                            key={activeTab}
                            onSelection={(sel: number) => {
                                handleSelection(sel);
                            }}
                            allowNull={categoryMap[activeTab]?.allowNull}
                        />
                    )}
                </div>
            </div>
        </>
    );
}
